import React, {useState, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet-async';
import {useLocation, useNavigate, useParams, NavLink} from 'react-router-dom';
import {Box, CardContent, Grid, Stack} from '@mui/material';

import {Breadcrumbs, Button, Chip, ModalZoomImage} from '../../../components';
import {DataDetail, typeRoutes} from '../../../types/typeAllTransaction';
import {path} from '../../../routes/path';
import {statusColor} from '../../../styles/status-color';
import ModalApproval from '../components/ModalApproval/ModalApproval';
import AllTransactionService from '../../../services/AllTransaction/all-transaction.service';
import {
  envRole,
  formatDate,
  formatLocalDateFromUTCTime,
  hasPermission,
  formatSingaporeDateTimeToISOString,
} from '../../../utils';
import {ColorType} from '../../../types/typeChip';
import {
  POINT_SUCCESS,
  POINT_SUCCESS_COLOR,
  POINT_SUCCESS_LABEL,
} from '../../../constants/status';
import queryString from 'query-string';

// styles
import {
  Typography,
  StyledDivBreadcrumbs,
  Card,
  Divider,
  StyledChip,
  StyledCardContent,
  ReceiptImage,
} from './AllTransactionDetail.style';
import {USER_TYPE} from '../../../constants/UserType';
import ModalApprovalReceipt from '../components/ModalApprovalReceipt/ModalApprovalReceipt';
import {
  DisplayFieldsLabel,
  OCRRejectReasonsLabel,
} from '../../../constants/receiptValidationLogsStatus';
import {IsNullOrEmptyOrUndefined} from '../../../utils/helpers';
import {TRANSACTION_TYPES_CONSTANTS} from '../../../constants/transaction';
import MembersService from '../../../services/Members/members.service';
import OcrHistoryService from '../../../services/OCRHistory/ocrHistory.service';
import useAuth from '../../../hooks/useAuth';
import {rbacPermissions} from '../../../constants/roleBasedAccessControlConstants';
import {
  renderRedemptionAmount,
  renderTransactionType,
} from '../AllTransactionUtil';

function AllTransactionDetail() {
  const {checkRbac} = useAuth();
  const navigate = useNavigate();
  const paramsUrl = useParams();
  const locationUrl = useLocation();
  const idDetail = paramsUrl?.id;
  const membershipId = locationUrl?.state?.membershipId;
  const merchantId = locationUrl?.state?.merchantId;
  const isAdministrator = +envRole === USER_TYPE.ADMINISTRATOR;
  const canUpdate = hasPermission(path.ISLAND_MEMBERS, 'update');
  const formatDateTime = 'DD/MM/YYYY HH:mm';
  // states
  const [isModalApproval, setIsModalApproval] = useState(false);
  const [isModalApprovalReceipt, setIsModalApprovalReceipt] = useState(false);
  const [dataDetail, setDataDetail] = useState<DataDetail>({});
  const [typeModal, setTypeModal] = useState('');
  const [vouchersUsedTitle, setVouchersUsedTitle] = useState<
    string | undefined | React.ReactNode
  >(undefined);
  const [isModalZoomImage, setIsModalZoomImage] = useState(false);
  const [imageZoom, setImageZoom] = useState('');
  const [memberPointsSummary, setMemberPointsSummary] = useState<any>(null);
  const [receiptDetail, setReceiptDetail] = useState<any>(null);

  const hasEditPermission = useMemo(() => {
    return (
      !!canUpdate &&
      checkRbac({
        permissionString: rbacPermissions.POINTS_TRANSACTIONS__LISTING__EDIT,
      })
    );
  }, [canUpdate, checkRbac]);

  const renderCaption = (value: string) => {
    if (value) {
      return (
        <Typography color="rgba(0, 0, 0, 0.5)" mb="31px" variant="h6">
          {value}:
        </Typography>
      );
    }
    return null;
  };

  const renderSubtitle = (
    value: React.ReactNode | string | null | undefined,
    tagName?: string
  ) => {
    const typographyProps: any = tagName
      ? {
          component: tagName,
        }
      : {};
    return (
      <Typography mb="31px" variant="h6" {...typographyProps}>
        {value ? value : '--'}
      </Typography>
    );
  };

  const renderRejectReason = (rejectReason: number | undefined) => {
    let title = '--';
    if (rejectReason !== null) {
      title = OCRRejectReasonsLabel[rejectReason - 1];
    }
    return renderSubtitle(title);
  };

  const renderRejectedFields = (rejectedFields: string) => {
    if (rejectedFields === null) {
      return (
        <Typography mb="31px" variant="h6">
          --
        </Typography>
      );
    }

    const splitRejectedFields = rejectedFields.split(',');

    if (splitRejectedFields.length === 1) {
      return renderSubtitle(DisplayFieldsLabel[splitRejectedFields[0]]);
    }

    return (
      <div>
        {splitRejectedFields.map((item, index) => (
          <li key={item + '-' + index}>
            {renderSubtitle(DisplayFieldsLabel[item], 'span')}
          </li>
        ))}
      </div>
    );
  };

  const renderTextStatus = (operation: number, point: number) => {
    const fontSize = '50px';
    if (point > 0) {
      return (
        <Typography
          color={operation === 1 ? statusColor.approved : statusColor.failed}
          variant="h1"
          fontSize={fontSize}
          mt={5}
          textAlign="center"
        >
          {operation === 1 ? `+ ${point}` : `- ${point}`}
        </Typography>
      );
    }
    return (
      <Typography
        color="#000000"
        variant="h1"
        fontSize={fontSize}
        mt={5}
        textAlign="center"
      >
        {point}
      </Typography>
    );
  };

  const toggleModalApproval = () => {
    if (dataDetail?.receiptId !== null) {
      setIsModalApprovalReceipt(!isModalApprovalReceipt);
    } else {
      setIsModalApproval(!isModalApproval);
    }
  };

  const getVoucherUsed = (data: any) => {
    const renderVoucherTitle = (item: any) => (
      <>
        [{item.membershipTicketId}] {item.ticketTitle}
        {item.ticketExcerpt && ` | ${item.ticketExcerpt}`}
      </>
    );
    let vouchersList =
      data?.transactionType === TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER
        ? data?.redeemedVoucher
        : data?.usedVouchers;
    if (!Array.isArray(vouchersList)) {
      vouchersList = [vouchersList];
    }
    if (vouchersList?.length > 0) {
      return setVouchersUsedTitle(
        <Grid container spacing={1}>
          {vouchersList.map((item: any, index: any) => (
            <Grid item key={index}>
              {isAdministrator && (
                <NavLink
                  style={{textDecoration: 'none'}}
                  to={`/${path.VOUCHER}/${item.ticketId}`}
                >
                  {renderVoucherTitle(item)}
                </NavLink>
              )}
              {!isAdministrator && (
                <Box sx={{}}>{renderVoucherTitle(item)}</Box>
              )}
            </Grid>
          ))}
        </Grid>
      );
    }

    return setVouchersUsedTitle(undefined);
  };

  const getDataDetail = () => {
    new AllTransactionService().getOne(idDetail).then(res => {
      if (res && res.data && res.data.data) {
        const transactionDetail = res.data.data;
        const isPendingTransaction =
          transactionDetail?.processId === POINT_SUCCESS.PENDING;
        const redirectOnBonusTransactionEdits =
          window?.sessionStorage?.getItem('redirectOnBonusTransactionEdits') ||
          'true';
        if (
          isAdministrator &&
          isPendingTransaction &&
          transactionDetail?.workflowId &&
          redirectOnBonusTransactionEdits === 'true'
        ) {
          navigate(
            `/${path.ALL_TRANSACTION}/${transactionDetail?.workflowId}${locationUrl?.search}`,
            {state: locationUrl?.state || {}}
          );
        } else {
          setDataDetail(transactionDetail);
          getVoucherUsed(transactionDetail);

          if (hasEditPermission && transactionDetail?.receiptId !== null) {
            new OcrHistoryService()
              .getAll({id_eq: transactionDetail?.receiptId})
              .then(apiRes => {
                if (apiRes?.success === true) {
                  if (
                    Array.isArray(apiRes?.data?.items) &&
                    apiRes?.data?.items.length > 0
                  ) {
                    setReceiptDetail(apiRes?.data?.items[0]);
                  }
                }
              });
          }
          if (hasEditPermission && transactionDetail?.membershipId !== null) {
            new MembersService()
              .getMemberPointsSummaryByDateTime(
                transactionDetail?.membershipId,
                formatSingaporeDateTimeToISOString(
                  transactionDetail?.createdDateTime
                )
              )
              .then(apiRes => {
                setMemberPointsSummary(apiRes?.data?.data);
              });
          }
        }
      }
    });
  };

  const renderSimilarTransactionIds = dataDetail => {
    if (
      dataDetail?.duplicatedTransactionIds !== null &&
      Array.isArray(dataDetail.duplicatedTransactionIds) &&
      dataDetail.duplicatedTransactionIds.length > 0
    ) {
      return dataDetail.duplicatedTransactionIds.map((item, index) => (
        <>
          {index > 0 && ', '}
          <NavLink
            style={{textDecoration: 'none'}}
            to={`/all-transaction/${item}`}
          >
            {item}
          </NavLink>
        </>
      ));
    }

    return '--';
  };

  const renderIslandPartnerName = (dataDetail: any = {}) => {
    const {islandPartnerId, islandPartnerName} = dataDetail;
    if (!islandPartnerName) return '--';
    let label = islandPartnerName;
    if (islandPartnerId) {
      label = `[${islandPartnerId}] ${label}`;
    }
    return label;
  };

  const renderSimilarReceiptIds = dataDetail => {
    if (
      dataDetail?.duplicatedReceiptIds !== null &&
      Array.isArray(dataDetail.duplicatedReceiptIds) &&
      dataDetail.duplicatedReceiptIds.length > 0
    ) {
      return dataDetail.duplicatedReceiptIds.map((item, index) => (
        <>
          {index > 0 && ', '}
          <NavLink
            style={{textDecoration: 'none'}}
            to={`/all-transaction/receipt-validation-logs?id_eq=${item}`}
          >
            {item}
          </NavLink>
        </>
      ));
    }

    return '--';
  };

  const renderLink = () => {
    if (membershipId) {
      return `/${
        path.ALL_TRANSACTION
      }?membershipId=${membershipId}${queryString.stringify(
        locationUrl?.state?.filterParams
      )}`;
    }
    if (merchantId) {
      return `/${
        path.ALL_TRANSACTION
      }?merchantId=${merchantId}${queryString.stringify(
        locationUrl?.state?.filterParams
      )}`;
    }
    return `/${path.ALL_TRANSACTION}?${queryString.stringify(
      locationUrl?.state?.filterParams
    )}`;
  };

  const renderStatusText = (value: number): string => {
    if (!IsNullOrEmptyOrUndefined(value)) {
      return POINT_SUCCESS_LABEL[value];
    }
    return '--';
  };

  const renderStatus = (value: number) => {
    if (!IsNullOrEmptyOrUndefined(value)) {
      const renderColor = POINT_SUCCESS_COLOR[value];
      const renderLabel = renderStatusText(value);
      return <Chip label={renderLabel} color={renderColor as ColorType} />;
    }
  };

  const renderSpentAmount = () => {
    const {transactionType, operation, islandPartnerName, spentAmount} =
      dataDetail || {};
    // If it's islander points redemtion, show '--' for spent amount
    if (
      transactionType ===
        TRANSACTION_TYPES_CONSTANTS.ISLANDER_POINTS_REDEMPTION ||
      (transactionType === TRANSACTION_TYPES_CONSTANTS.GET_VOUCHER &&
        (isNaN(spentAmount as number) ||
          [null, undefined, 0].includes(Number(spentAmount))))
    ) {
      return '--';
    }
    if (operation === 2 && islandPartnerName === null) {
      return '$ 0';
    }
    return spentAmount
      ? `$ ${parseFloat(spentAmount?.toString()).toFixed(2)}`
      : '--';
  };

  const renderExcessRedemptionAmount = () => {
    if (dataDetail?.excessAmount !== null) {
      return parseFloat(dataDetail?.excessAmount?.toString() ?? '').toFixed(2);
    }

    return '--';
  };

  const ValidateUsedOrRemovedVoucher = (value: any) => {
    return (
      value?.expiryDateTime === null &&
      value?.spentAmount === null &&
      value?.pointValue === null &&
      value?.merchantId === null &&
      value?.employeeId === null &&
      value?.ticketId !== null
    );
  };

  const toggleIsModalZoomImage = () => {
    setIsModalZoomImage(!isModalZoomImage);
  };

  const renderReceiptImage = image => {
    if (!image) {
      return '--';
    }
    return (
      <ReceiptImage
        src={image}
        onClick={() => {
          toggleIsModalZoomImage();
          setImageZoom(image);
        }}
      />
    );
  };

  const renderSystemAssuranceLevel = (values: any) => {
    const percent =
      values?.receiptConfidence !== null
        ? values.receiptConfidence * 100
        : null;
    let color = '#6E6E6E';
    if (percent === null) {
      return (
        <Typography
          mb="31px"
          variant="h6"
          color={'GrayText'}
          fontWeight="bolder"
        >
          --
        </Typography>
      );
    }

    if (percent < 80) {
      color = '#E66A25';
    } else if (percent >= 80) {
      color = '#2E7D32';
    }
    return (
      <Typography
        mb="31px"
        variant="h6"
        sx={{color: color}}
      >{`${percent}%`}</Typography>
    );
  };

  const renderRowColumnsA = (
    title: React.ReactNode | string | null,
    value: React.ReactNode | string
  ) => {
    return (
      <tr>
        <td width="34%" style={{verticalAlign: 'top'}}>
          {title}
        </td>
        <td width="64%" style={{verticalAlign: 'top'}}>
          {value}
        </td>
      </tr>
    );
  };

  const renderRowColumnsB = (
    title: React.ReactNode | string | null,
    value: React.ReactNode | string
  ) => {
    return (
      <tr>
        <td width="50%" style={{verticalAlign: 'top'}}>
          {title}
        </td>
        <td width="50%" style={{verticalAlign: 'top'}}>
          {value}
        </td>
      </tr>
    );
  };

  useEffect(() => {
    getDataDetail();
  }, [idDetail]);

  return (
    <React.Fragment>
      <Helmet title="Dashboard" />

      <ModalApproval
        title={
          typeModal === 'approve'
            ? 'Before you approve'
            : 'You’re about to reject this transaction.'
        }
        typeModal={typeModal}
        isModalApproval={isModalApproval}
        onClose={toggleModalApproval}
        dataDetail={dataDetail}
        reloadPage={getDataDetail}
        // setDataDetail={setDataDetail}
      />

      <ModalApprovalReceipt
        title={
          typeModal === 'approve'
            ? 'Before you approve'
            : 'You’re about to reject this transaction.'
        }
        typeModal={typeModal}
        isModalApprovalReceipt={isModalApprovalReceipt}
        onClose={toggleModalApproval}
        dataDetail={dataDetail}
        reloadPage={getDataDetail}
        memberPointsSummary={memberPointsSummary}
        receiptDetail={receiptDetail}
        // setDataDetail={setDataDetail}
      />

      <ModalZoomImage
        visible={isModalZoomImage}
        image={imageZoom}
        alt="All Transaction Detail Image Zoom"
        onClose={toggleIsModalZoomImage}
      />

      <Typography variant="h3" gutterBottom>
        Transaction Detail
      </Typography>
      <StyledDivBreadcrumbs>
        <Breadcrumbs
          routes={[
            // typeRoutes('Dashboard', `/${path.DASHBOARD}`),
            typeRoutes(
              'All Transaction',
              checkRbac({
                permissionString:
                  rbacPermissions.POINTS_TRANSACTIONS__LISTING__VIEW,
              })
                ? renderLink()
                : undefined,
              false,
              {
                membershipId: membershipId,
                merchantId: merchantId,
              }
            ),
            typeRoutes('Detail', null, true),
          ]}
        />
      </StyledDivBreadcrumbs>

      <Divider my={6} />

      <Grid container>
        <Grid item xs={12} md={12} display="flex" justifyContent="center">
          <Typography variant="h6" fontSize="14px">
            {formatLocalDateFromUTCTime(
              dataDetail?.createdDateTime,
              'H:mm A, MMM DD, YYYY'
            ) || '--'}
          </Typography>
        </Grid>
      </Grid>

      <Grid container mb={9}>
        <Grid item xs={12} md={12}>
          <StyledChip>{renderStatus(dataDetail.processId)}</StyledChip>
          <Card
            pl={12}
            pr={12}
            sx={{
              position: 'relative',
              bottom: '16px',
            }}
          >
            <StyledCardContent>
              <Grid container direction="row" justifyContent="space-evenly">
                <Grid item>
                  {renderTextStatus(
                    dataDetail?.operation,
                    dataDetail?.pointValue
                  )}
                  <Typography
                    variant="h6"
                    mt="10px"
                    textAlign="center"
                    fontStyle="oblique"
                  >
                    {dataDetail?.notes || '--'}
                  </Typography>
                  <Typography
                    variant="h6"
                    textAlign="center"
                    color="rgba(0, 0, 0, 0.5)"
                  >
                    {`# ${dataDetail?.id}`}
                  </Typography>
                </Grid>
              </Grid>
              <Divider my={6} />
              <Grid wrap="nowrap" container justifyContent="space-evenly">
                <table width="50%" style={{height: 'fit-content'}}>
                  <tbody>
                    {renderRowColumnsA(
                      +envRole === USER_TYPE.ADMINISTRATOR &&
                        renderCaption('Islander Member’s Email'),
                      +envRole === USER_TYPE.ADMINISTRATOR &&
                        renderSubtitle(dataDetail?.email)
                    )}
                    {renderRowColumnsA(
                      renderCaption('Membership ID'),
                      renderSubtitle(dataDetail?.customerId)
                    )}
                    {isAdministrator &&
                      renderRowColumnsA(
                        renderCaption('Loyalty Membership ID'),
                        renderSubtitle(dataDetail?.membershipId)
                      )}
                    {renderRowColumnsA(
                      renderCaption('Transaction type'),
                      renderSubtitle(renderTransactionType(dataDetail, true))
                    )}
                    {![
                      TRANSACTION_TYPES_CONSTANTS.USE_VOUCHERS,
                      TRANSACTION_TYPES_CONSTANTS.ISLANDER_POINTS_REDEMPTION,
                    ].includes(Number(dataDetail?.transactionType)) &&
                      renderRowColumnsA(
                        renderCaption('Amount spent'),
                        renderSubtitle(renderSpentAmount())
                      )}
                    {[
                      TRANSACTION_TYPES_CONSTANTS.USE_VOUCHERS,
                      TRANSACTION_TYPES_CONSTANTS.ISLANDER_POINTS_REDEMPTION,
                    ].includes(Number(dataDetail?.transactionType)) &&
                      renderRowColumnsA(
                        renderCaption('Redemption Amount'),
                        renderSubtitle(renderRedemptionAmount(dataDetail))
                      )}
                    {[
                      TRANSACTION_TYPES_CONSTANTS.USE_VOUCHERS,
                      TRANSACTION_TYPES_CONSTANTS.ISLANDER_POINTS_REDEMPTION,
                    ].includes(dataDetail?.transactionType as number) &&
                      renderRowColumnsA(
                        renderCaption('Excess redemption amount'),
                        renderSubtitle(renderExcessRedemptionAmount())
                      )}
                    {renderRowColumnsA(
                      renderCaption('Receipt Image'),
                      renderSubtitle(
                        renderReceiptImage(dataDetail?.receiptImageUrl ?? null)
                      )
                    )}
                    {isAdministrator &&
                      renderRowColumnsA(
                        renderCaption('System Assurance Level'),
                        renderSystemAssuranceLevel(dataDetail)
                      )}
                    {isAdministrator &&
                      renderRowColumnsA(
                        renderCaption('Similar to ≥$20 transaction ID(s)'),
                        renderSubtitle(renderSimilarTransactionIds(dataDetail))
                      )}
                    {isAdministrator &&
                      renderRowColumnsA(
                        renderCaption('Similar to <$20 transaction ID(s)'),
                        renderSubtitle(renderSimilarReceiptIds(dataDetail))
                      )}
                  </tbody>
                </table>
                <Grid item xs={1} md={1}></Grid>
                <table width="35%" style={{height: 'fit-content'}}>
                  <tbody>
                    {isAdministrator &&
                      renderRowColumnsB(
                        renderCaption('Points Expiry Date'),
                        renderSubtitle(
                          formatDate(dataDetail?.expiryDateTime, formatDateTime)
                        )
                      )}
                    {renderRowColumnsB(
                      renderCaption('Island Partner'),
                      renderSubtitle(renderIslandPartnerName(dataDetail))
                    )}
                    {renderRowColumnsB(
                      renderCaption('Authorised by'),
                      renderSubtitle(dataDetail?.employeeName)
                    )}
                    {renderRowColumnsB(
                      renderCaption('Voucher detail(s)'),
                      renderSubtitle(vouchersUsedTitle)
                    )}
                    {renderRowColumnsB(
                      renderCaption('Points multiplier event name'),
                      renderSubtitle(dataDetail?.pointMultiplierEventTitle)
                    )}
                    {renderRowColumnsB(
                      renderCaption('Associated Transaction No'),
                      renderSubtitle(dataDetail?.workflowId)
                    )}
                  </tbody>
                </table>
              </Grid>
              {dataDetail.processId === 3 && (
                <>
                  <Divider my={6} />
                  <Typography variant="h2" sx={{mb: 8}}>
                    Review details
                  </Typography>
                  <Grid wrap="nowrap" container justifyContent="space-evenly">
                    <table width="50%" style={{height: 'fit-content'}}>
                      <tbody>
                        {renderRowColumnsA(
                          renderCaption('Status'),
                          renderSubtitle(renderStatusText(dataDetail.processId))
                        )}
                        {renderRowColumnsA(
                          renderCaption('Reason for rejection'),
                          renderRejectReason(dataDetail?.rejectReason)
                        )}
                        {renderRowColumnsA(
                          renderCaption('Details for rejection'),
                          renderRejectedFields(
                            dataDetail?.displayFields as string
                          )
                        )}
                      </tbody>
                    </table>
                    <Grid item xs={1} md={1}></Grid>
                    <table width="35%" style={{height: 'fit-content'}} />
                  </Grid>
                </>
              )}
            </StyledCardContent>
          </Card>
        </Grid>
        <Divider my={6} />
      </Grid>
      <Grid container>
        <Grid item xs={12} lg={12}>
          <Card px={6} pt={3}>
            <CardContent>
              <Grid container direction="row" justifyContent="center">
                <Stack direction="row" spacing={4}>
                  {dataDetail?.processId === 2 && hasEditPermission ? (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        type="button"
                        onClick={() => {
                          toggleModalApproval();
                          setTypeModal('approve');
                        }}
                        width="125px"
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        type="button"
                        onClick={() => {
                          toggleModalApproval();
                          setTypeModal('declined');
                        }}
                        width="125px"
                      >
                        Decline
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        type="button"
                        onClick={() => {
                          navigate(-1);
                        }}
                        width="125px"
                      >
                        Done
                      </Button>
                      {/* {(+envRole === USER_TYPE.ADMINISTRATOR &&
                        dataDetail?.operation === 2 &&
                        dataDetail?.islandPartnerName === null) ||
                      (+envRole === USER_TYPE.ADMINISTRATOR &&
                        dataDetail?.ticketId !== null &&
                        dataDetail?.islandPartnerName !== null) ||
                      (+envRole === USER_TYPE.ADMINISTRATOR &&
                        ValidateUsedOrRemovedVoucher(dataDetail))
                        ? checkRbac(
                            {
                              permissionString: [
                                rbacPermissions.VOUCHERS__LISTING__VIEW,
                                rbacPermissions.VOUCHERS__LISTING__EDIT,
                                rbacPermissions.VOUCHERS__LISTING__DELETE,
                              ],
                            },
                            <Button
                              variant="contained"
                              color="primary"
                              type="button"
                              onClick={() => {
                                navigate(
                                  `/${path.VOUCHER}/${dataDetail?.ticketId}`
                                );
                              }}
                              width="185px"
                            >
                              {`Go to Voucher ID ${dataDetail?.ticketNumberId}`}
                            </Button>
                          )
                        : null} */}
                      {/* {(+envRole === USER_TYPE.ADMINISTRATOR &&
                        dataDetail?.operation === 2 &&
                        dataDetail?.islandPartnerName === null &&
                        dataDetail?.secondTicketId !== null) ||
                      (+envRole === USER_TYPE.ADMINISTRATOR &&
                        dataDetail?.secondTicketId !== null &&
                        dataDetail?.islandPartnerName !== null)
                        ? checkRbac(
                            {
                              permissionString: [
                                rbacPermissions.VOUCHERS__LISTING__VIEW,
                                rbacPermissions.VOUCHERS__LISTING__EDIT,
                                rbacPermissions.VOUCHERS__LISTING__DELETE,
                              ],
                            },
                            <Button
                              variant="contained"
                              color="primary"
                              type="button"
                              onClick={() => {
                                navigate(
                                  `/${path.VOUCHER}/${dataDetail?.secondTicketId}`
                                );
                              }}
                              width="185px"
                            >
                              {`Go to Voucher ID ${dataDetail?.secondTicketNumberId}`}
                            </Button>
                          )
                        : null} */}

                      {dataDetail?.pointMultiplierEventCreationId &&
                      isAdministrator
                        ? checkRbac(
                            {
                              permissionString:
                                rbacPermissions.POINTS_MULTIPLIER_EVENT__LISTING__VIEW,
                            },
                            <Button
                              variant="contained"
                              color="primary"
                              type="button"
                              onClick={() => {
                                navigate(
                                  `/${path.LIMITED_EVENT}?_page=0&_size=10&status_eq=1&limitedEventId_eq=${dataDetail?.pointMultiplierEventCreationId}`
                                );
                              }}
                              width="165px"
                            >
                              Go to Limited Event
                            </Button>
                          )
                        : ''}
                    </>
                  )}
                </Stack>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default AllTransactionDetail;
